import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Alert,
  AlertDescription,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { toast } from 'sonner';
import {
  DataTable,
  PaginationControls,
  SearchBar,
  TABLE_PAGE_SIZE_OPTIONS,
  type DataTableColumn,
  type SortDirection,
} from '../components/ListControls';

// ─── Types ──────────────────────────────────────────────────────────────────────

type ClaimStatus = 'verified' | 'pending' | 'expired' | 'not-detected';
type TokenSortKey = 'token' | 'balanceNum' | 'valueNum' | 'change24h' | 'chain';

interface OnchainClaim {
  id: string;
  label: string;
  description: string;
  status: ClaimStatus;
  issuer: string;
  issuedAt?: string;
  expiresAt?: string;
  claimHash?: string;
  icon: React.ReactNode;
}

interface TokenBalance {
  token: string;
  symbol: string;
  balance: string;
  balanceNum: number;
  valueUsd: string;
  valueNum: number;
  change24h: number;
  chain: string;
}

interface OnchainId {
  id: string;
  address: string;
  registry: string;
  source: 'ascend' | 'external' | 'erc3643';
  claimCount: number;
  createdAt: string;
}

// ─── Mock Data ──────────────────────────────────────────────────────────────────

const INITIAL_ONCHAIN_IDS: OnchainId[] = [
  {
    id: 'oid-ascend-001',
    address: '0x1b75eb4a8d1F9C8E5C49Ab3c7F2dD59a9C0ef94e',
    registry: 'Ascend Identity Registry',
    source: 'ascend',
    claimCount: 5,
    createdAt: '2025-08-15',
  },
  {
    id: 'oid-erc3643-001',
    address: '0x1b75eb4a8d1F9C8E5C49Ab3c7F2dD59a9C0ef94e',
    registry: 'ERC-3643 Token Registry',
    source: 'erc3643',
    claimCount: 2,
    createdAt: '2025-10-22',
  },
];

const WALLET_INFO = {
  address: '0x1b75eb4a8d1F9C8E5C49Ab3c7F2dD59a9C0ef94e',
  shortAddress: '0x1b75eb...f94e',
  ens: 'alice.ascend.eth',
  chain: 'Ethereum Mainnet',
  chainId: 1,
  networkIcon: '🔷',
  connectedSince: '2025-08-14',
  lastActivity: '2026-03-23 09:42 UTC',
  nonce: 847,
};

const TOKEN_BALANCES: TokenBalance[] = [
  { token: 'USD Coin', symbol: 'USDC', balance: '12,450.00', balanceNum: 12450, valueUsd: '$12,450.00', valueNum: 12450, change24h: 0.01, chain: 'Ethereum' },
  { token: 'Ascend Bonded Note', symbol: 'ABN', balance: '2.5000', balanceNum: 2.5, valueUsd: '$137,500.00', valueNum: 137500, change24h: 3.42, chain: 'Ethereum' },
  { token: 'SingNet Token', symbol: 'SNG', balance: '10,000.00', balanceNum: 10000, valueUsd: '$85,000.00', valueNum: 85000, change24h: -1.18, chain: 'Ethereum' },
  { token: 'Financial Market Maker', symbol: 'FMM', balance: '45.2500', balanceNum: 45.25, valueUsd: '$1,810.00', valueNum: 1810, change24h: 0.75, chain: 'Ethereum' },
  { token: 'Apex Protocol', symbol: 'APX', balance: '0.0000', balanceNum: 0, valueUsd: '$0.00', valueNum: 0, change24h: -2.35, chain: 'Ethereum' },
  { token: 'Tether USD', symbol: 'USDT', balance: '8,750.00', balanceNum: 8750, valueUsd: '$8,750.00', valueNum: 8750, change24h: 0.02, chain: 'Ethereum' },
  { token: 'USD1 Stablecoin', symbol: 'USD1', balance: '5,230.18', balanceNum: 5230.18, valueUsd: '$5,230.18', valueNum: 5230.18, change24h: -0.02, chain: 'Ethereum' },
  { token: 'Stability Bond Coin', symbol: 'SBC', balance: '150.0000', balanceNum: 150, valueUsd: '$22,500.00', valueNum: 22500, change24h: 0.33, chain: 'Ethereum' },
  { token: 'Meridian Share', symbol: 'MSM', balance: '800.0000', balanceNum: 800, valueUsd: '$5,600.00', valueNum: 5600, change24h: -0.87, chain: 'Ethereum' },
];

const ONCHAIN_CLAIMS: OnchainClaim[] = [
  {
    id: 'claim-kyc',
    label: 'KYC Verification',
    description: 'Identity verified through accredited KYC provider',
    status: 'verified',
    issuer: 'Ascend',
    issuedAt: '2025-09-01',
    expiresAt: '2026-09-01',
    claimHash: '0x4a7f...c2e1',
    icon: <Icon name="streamline:fingerprint-2" className="h-5 w-5" />,
  },
  {
    id: 'claim-jurisdiction',
    label: 'Jurisdiction Approval',
    description: 'Operating jurisdiction approved for DeFi participation',
    status: 'verified',
    issuer: 'Ascend',
    issuedAt: '2025-09-01',
    expiresAt: '2026-09-01',
    claimHash: '0x8b3e...a4f7',
    icon: <Icon name="streamline:travel-map-location-pin-navigation-map-maps-pin-gps-location" className="h-5 w-5" />,
  },
  {
    id: 'claim-accredited',
    label: 'Accredited Investor',
    description: 'Verified as accredited investor under applicable regulations',
    status: 'verified',
    issuer: 'Ascend',
    issuedAt: '2025-09-15',
    expiresAt: '2026-09-15',
    claimHash: '0x2d9a...7bc3',
    icon: <Icon name="streamline:shield-check" className="h-5 w-5" />,
  },
  {
    id: 'claim-aml',
    label: 'AML Screening',
    description: 'Anti-money laundering screening passed',
    status: 'verified',
    issuer: 'Chainalysis Oracle',
    issuedAt: '2025-10-12',
    expiresAt: '2026-04-12',
    claimHash: '0x6c1d...e5a8',
    icon: <Icon name="streamline:interface-security-shield-1-shield-protection-security-defend-crime-war-cover" className="h-5 w-5" />,
  },
  {
    id: 'claim-sanctions',
    label: 'Sanctions Screening',
    description: 'Address not found on OFAC or EU sanctions lists',
    status: 'verified',
    issuer: 'Chainalysis Oracle',
    issuedAt: '2026-03-01',
    expiresAt: '2026-06-01',
    claimHash: '0x9e4b...1d72',
    icon: <Icon name="streamline:legal-justice-scale-1-office-work-legal-scale-justice-company-arbitration-balance-court" className="h-5 w-5" />,
  },
  {
    id: 'claim-credit-score',
    label: 'On-Chain Credit Score',
    description: 'DeFi credit history assessment across protocols',
    status: 'verified',
    issuer: 'Spectral Finance',
    issuedAt: '2026-02-15',
    claimHash: '0xa3f2...8e46',
    icon: <Icon name="streamline:graph-bar-increase" className="h-5 w-5" />,
  },
  {
    id: 'claim-tax-residency',
    label: 'Tax Residency Declaration',
    description: 'Self-declared tax residency for regulatory reporting',
    status: 'pending',
    issuer: 'Ascend',
    icon: <Icon name="streamline:interface-file-text-text-common-file" className="h-5 w-5" />,
  },
  {
    id: 'claim-institutional',
    label: 'Institutional Accreditation',
    description: 'Institutional-grade participant verification',
    status: 'not-detected',
    issuer: 'Ascend Governance',
    icon: <Icon name="streamline:bank" className="h-5 w-5" />,
  },
];

const CLAIM_STATUS_CONFIG: Record<ClaimStatus, { label: string; className: string; icon: React.ReactNode }> = {
  verified: { label: 'Verified', className: 'bg-green-100 text-green-700', icon: <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-3.5 w-3.5" /> },
  pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700', icon: <Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="h-3.5 w-3.5" /> },
  expired: { label: 'Expired', className: 'bg-red-100 text-red-700', icon: <Icon name="streamline:warning-triangle" className="h-3.5 w-3.5" /> },
  'not-detected': { label: 'Not Detected', className: 'bg-gray-100 text-gray-500', icon: <Icon name="streamline:interface-delete-circle-button-delete-remove-add-circle-buttons" className="h-3.5 w-3.5" /> },
};

// ─── Page Component ─────────────────────────────────────────────────────────────

export function ProfilePage() {
  const navigate = useNavigate();
  const [showAllTokens, setShowAllTokens] = useState(false);
  const [onchainIds, setOnchainIds] = useState<OnchainId[]>(INITIAL_ONCHAIN_IDS);
  const [isCreatingId, setIsCreatingId] = useState(false);
  const [demoHasIds, setDemoHasIds] = useState(true);
  const [tokenQuery, setTokenQuery] = useState('');
  const [tokenChainFilter, setTokenChainFilter] = useState('all');
  const [tokenSortKey, setTokenSortKey] = useState<TokenSortKey>('valueNum');
  const [tokenSortDirection, setTokenSortDirection] = useState<SortDirection>('desc');
  const [tokenPage, setTokenPage] = useState(1);
  const [tokenPageSize, setTokenPageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[0]);
  const [expandedHash, setExpandedHash] = useState<string | null>(null);

  const displayedIds = demoHasIds ? onchainIds : [];

  function handleCreateOnchainId() {
    setIsCreatingId(true);
    setTimeout(() => {
      const newId: OnchainId = {
        id: `oid-ascend-${Date.now()}`,
        address: WALLET_INFO.address,
        registry: 'Ascend Identity Registry',
        source: 'ascend',
        claimCount: 0,
        createdAt: new Date().toISOString().split('T')[0],
      };
      setOnchainIds((prev) => [...prev, newId]);
      setDemoHasIds(true);
      setIsCreatingId(false);
      toast.success('OnchainID created on Ascend Identity Registry');
    }, 2500);
  }

  const tokenChains = useMemo(
    () => ['all', ...new Set(TOKEN_BALANCES.map((token) => token.chain))],
    [],
  );

  const filteredTokens = useMemo(() => {
    let items = showAllTokens ? TOKEN_BALANCES : TOKEN_BALANCES.filter((t) => t.balanceNum > 0);
    if (tokenChainFilter !== 'all') items = items.filter((token) => token.chain === tokenChainFilter);
    if (tokenQuery.trim()) {
      const q = tokenQuery.toLowerCase();
      items = items.filter(
        (token) =>
          token.token.toLowerCase().includes(q)
          || token.symbol.toLowerCase().includes(q)
          || token.chain.toLowerCase().includes(q),
      );
    }
    return items;
  }, [showAllTokens, tokenChainFilter, tokenQuery]);

  const sortedTokens = useMemo(() => {
    const items = [...filteredTokens];
    items.sort((a, b) => {
      let comparison = 0;
      switch (tokenSortKey) {
        case 'token':
          comparison = a.symbol.localeCompare(b.symbol);
          break;
        case 'balanceNum':
          comparison = a.balanceNum - b.balanceNum;
          break;
        case 'valueNum':
          comparison = a.valueNum - b.valueNum;
          break;
        case 'change24h':
          comparison = a.change24h - b.change24h;
          break;
        case 'chain':
          comparison = a.chain.localeCompare(b.chain);
          break;
      }
      return tokenSortDirection === 'asc' ? comparison : -comparison;
    });
    return items;
  }, [filteredTokens, tokenSortDirection, tokenSortKey]);

  const paginatedTokens = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(sortedTokens.length / tokenPageSize));
    const safePage = Math.min(tokenPage, totalPages);
    return sortedTokens.slice((safePage - 1) * tokenPageSize, safePage * tokenPageSize);
  }, [sortedTokens, tokenPage, tokenPageSize]);

  // Reset to page 1 when filters/sort change.
  const tokenFilterKey = `${showAllTokens}|${tokenChainFilter}|${tokenQuery}|${tokenSortKey}|${tokenSortDirection}`;
  const [prevTokenFilterKey, setPrevTokenFilterKey] = useState(tokenFilterKey);
  if (prevTokenFilterKey !== tokenFilterKey) {
    setPrevTokenFilterKey(tokenFilterKey);
    setTokenPage(1);
  }

  // Layout-level redirect handles unauthenticated access

  const totalPortfolioValue = TOKEN_BALANCES.reduce((sum, t) => sum + t.valueNum, 0);
  const verifiedClaims = ONCHAIN_CLAIMS.filter((c) => c.status === 'verified').length;
  const totalClaims = ONCHAIN_CLAIMS.length;

  function onSortTokens(nextKey: TokenSortKey) {
    setTokenSortDirection((prev) => (
      tokenSortKey === nextKey ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'
    ));
    setTokenSortKey(nextKey);
    setTokenPage(1);
  }

  function copyAddress() {
    navigator.clipboard.writeText(WALLET_INFO.address);
    toast.success('Address copied to clipboard');
  }

  const tokenColumns = useMemo<DataTableColumn<TokenBalance, TokenSortKey>[]>(
    () => [
      {
        id: 'token',
        label: 'Token',
        sortable: true,
        sortKey: 'token',
        renderCell: (token) => (
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary flex-shrink-0">
              {token.symbol.slice(0, 2)}
            </div>
            <div>
              <span className="font-medium">{token.symbol}</span>
              <span className="ml-2 text-xs text-muted-foreground">{token.token}</span>
            </div>
          </div>
        ),
      },
      {
        id: 'balanceNum',
        label: 'Balance',
        align: 'right',
        sortable: true,
        sortKey: 'balanceNum',
        cellClassName: 'font-mono text-xs',
        renderCell: (token) => token.balance,
      },
      {
        id: 'valueNum',
        label: 'Value (USD)',
        align: 'right',
        sortable: true,
        sortKey: 'valueNum',
        cellClassName: 'font-medium',
        renderCell: (token) => token.valueUsd,
      },
      {
        id: 'change24h',
        label: '24h Change',
        align: 'right',
        sortable: true,
        sortKey: 'change24h',
        renderCell: (token) => (
          <span className={`text-xs font-medium ${token.change24h >= 0 ? 'text-ascend-success' : 'text-ascend-error'}`}>
            {token.change24h >= 0 ? '+' : ''}
            {token.change24h.toFixed(2)}%
          </span>
        ),
      },
      {
        id: 'chain',
        label: 'Chain',
        align: 'center',
        sortable: true,
        sortKey: 'chain',
        renderCell: (token) => <span className="text-xs text-muted-foreground">{token.chain}</span>,
      },
    ],
    [],
  );

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex items-start gap-3">
        <Icon name="streamline:interface-user-circle-circle-geometric-human-person-single-user" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Profile</h1>
          <p className="text-sm text-muted-foreground">
            Your wallet, onchain identity, balances, and position overview.
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div id="actions" className="scroll-mt-28 rounded-xl border border-border p-5 sm:p-6">
        <h3 className="mb-4 text-lg font-semibold">Quick Actions</h3>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-6 md:gap-3">
          {[
            { label: 'Deposit', iconName: 'streamline:coins-stack', route: '/deposit' },
            { label: 'Borrow', iconName: 'streamline:bank', route: '/borrow' },
            { label: 'Withdraw', iconName: 'streamline:interface-arrows-button-to-top-arrow-up-line-to-top', route: '/withdraw' },
            { label: 'Repay', iconName: 'streamline:interface-arrows-button-to-bottom-arrow-down-line-to-bottom', route: '/repay' },
            { label: 'Lend', iconName: 'streamline:graph-bar-increase', route: '/lender/deposit' },
            { label: 'History', iconName: 'streamline:interface-time-clock-circle-clock-loading-measure-time-circle', route: '/history' },
          ].map((action) => (
            <button
              key={action.label}
              onClick={() => navigate(action.route)}
              className="flex flex-col items-center gap-2 rounded-xl border border-border p-3 sm:p-4 text-xs sm:text-sm font-medium hover:bg-accent transition-colors"
            >
              <Icon name={action.iconName} className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />
              {action.label}
            </button>
          ))}
        </div>
      </div>

      {/* Wallet Identity Card */}
      <Card id="wallet" className="scroll-mt-28">
        <CardHeader className="p-6">
          <CardTitle>Wallet Identity</CardTitle>
          <CardDescription>Connected wallet details and network information.</CardDescription>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary flex-shrink-0">
                  <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  {WALLET_INFO.ens && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg font-bold">{WALLET_INFO.ens}</span>
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">ENS</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <code className="rounded-md bg-muted px-2.5 py-1 font-mono text-xs sm:text-sm break-all">
                      {WALLET_INFO.address}
                    </code>
                    <button onClick={copyAddress} className="rounded-md p-1.5 text-muted-foreground hover:text-foreground transition-colors" title="Copy address">
                      <Icon name="streamline:interface-edit-copy-clipboard-copy-cut-paste" className="h-4 w-4" />
                    </button>
                    <a href={`https://etherscan.io/address/${WALLET_INFO.address}`} target="_blank" rel="noopener noreferrer" className="rounded-md p-1.5 text-muted-foreground hover:text-foreground transition-colors" title="View on Etherscan">
                      <Icon name="streamline:share-link" className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <InfoItem icon={<Icon name="streamline:travel-map-earth-2-planet-earth-globe-world" className="h-4 w-4" />} label="Network" value={WALLET_INFO.chain} />
              <InfoItem icon={<Icon name="streamline:interface-edit-attachment-2-attachment-link-paperclip-unlink" className="h-4 w-4" />} label="Chain ID" value={WALLET_INFO.chainId.toString()} />
              <InfoItem icon={<Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="h-4 w-4" />} label="Connected Since" value={WALLET_INFO.connectedSince} />
              <InfoItem icon={<Icon name="streamline:heart-rate-pulse-graph" className="h-4 w-4" />} label="Last Activity" value={WALLET_INFO.lastActivity} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Portfolio Overview Stats */}
      <div id="overview" className="scroll-mt-28 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:gap-4">
        <StatCard title="Portfolio Value" value={formatUsd(totalPortfolioValue)} icon={<Icon name="streamline:coins-stack" className="h-5 w-5 text-muted-foreground" />} />
        <StatCard title="Verified Claims" value={`${verifiedClaims}/${totalClaims}`} icon={<Icon name="streamline:shield-check" className="h-5 w-5 text-ascend-success" />} />
      </div>

      {/* Onchain Identity */}
      <Card id="identity" className="scroll-mt-28">
        <CardHeader className="p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Onchain Identity</CardTitle>
              <CardDescription>
                OnchainIDs linked to your wallet address. These are identity contracts registered
                on Ascend Identity Registry, ERC-3643 Token Registries, or other whitelisted registries.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                {displayedIds.length} detected
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 pt-0 space-y-4">
          {/* Demo control */}
          <div className="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/30 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground font-medium">Demo: Simulate OnchainID detection</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setDemoHasIds(true)}
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${demoHasIds ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                >
                  Has IDs
                </button>
                <button
                  onClick={() => setDemoHasIds(false)}
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${!demoHasIds ? 'bg-yellow-100 text-yellow-700' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                >
                  No IDs
                </button>
              </div>
            </div>
          </div>

          {displayedIds.length === 0 ? (
            <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-5 space-y-4">
              <div className="flex items-start gap-3">
                <Icon name="streamline:warning-triangle" className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">No OnchainID Detected</p>
                  <p className="text-xs text-yellow-700 mt-1">
                    No identity was found for your wallet address across any registered identity
                    registry. An OnchainID is required to access credit facilities and interact
                    with the protocol.
                  </p>
                </div>
              </div>
              <div className="rounded-lg border border-yellow-300 bg-white/60 p-4 space-y-3">
                <div className="flex items-start gap-2.5">
                  <Icon name="streamline:fingerprint-2" className="h-4 w-4 text-yellow-700 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-yellow-900">Create an OnchainID</p>
                    <p className="text-xs text-yellow-700 mt-0.5">
                      Mint a new OnchainID on the Ascend Identity Registry. This deploys an identity
                      contract linked to your wallet address. Once created, you can attach verifiable
                      claims (KYC, accreditation, jurisdiction) to meet credit facility requirements.
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  disabled={isCreatingId}
                  onClick={handleCreateOnchainId}
                >
                  {isCreatingId ? (
                    <><Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-4 w-4 mr-1.5 animate-spin" /> Creating OnchainID...</>
                  ) : (
                    <><Icon name="streamline:interface-add-1-expand-cross-buttons-button-more-remove-plus-add" className="h-4 w-4 mr-1.5" /> Create OnchainID on Ascend Registry</>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {displayedIds.map((oid) => {
                const sourceColors: Record<string, string> = {
                  ascend: 'bg-primary/10 text-primary',
                  erc3643: 'bg-blue-100 text-blue-700',
                  external: 'bg-purple-100 text-purple-700',
                };
                return (
                  <div key={oid.id} className="rounded-xl border border-border p-4 hover:bg-accent/30 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <Icon name="streamline:fingerprint-2" className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm font-mono truncate">{oid.id}</span>
                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${sourceColors[oid.source] ?? 'bg-gray-100 text-gray-600'}`}>
                              {oid.source === 'ascend' ? 'Ascend' : oid.source === 'erc3643' ? 'ERC-3643' : 'External'}
                            </span>
                          </div>
                          <p className="mt-0.5 text-xs text-muted-foreground">{oid.registry}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${oid.claimCount > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {oid.claimCount} claim{oid.claimCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>Address: <code className="font-mono text-foreground">{oid.address.slice(0, 10)}...{oid.address.slice(-4)}</code></span>
                      <span>Created: {oid.createdAt}</span>
                    </div>
                  </div>
                );
              })}

              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isCreatingId}
                  onClick={handleCreateOnchainId}
                >
                  {isCreatingId ? (
                    <><Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-4 w-4 mr-1.5 animate-spin" /> Creating...</>
                  ) : (
                    <><Icon name="streamline:interface-add-1-expand-cross-buttons-button-more-remove-plus-add" className="h-4 w-4 mr-1.5" /> Create Additional OnchainID</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Onchain Claims */}
      <Card id="claims" className="scroll-mt-28">
        <CardHeader className="p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Onchain Claims</CardTitle>
              <CardDescription>Verifiable claims detected for this wallet address. Claims are issued by trusted attestors and stored onchain.</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                {verifiedClaims} Verified
              </span>
              {ONCHAIN_CLAIMS.some((c) => c.status === 'pending') && (
                <span className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-700">
                  {ONCHAIN_CLAIMS.filter((c) => c.status === 'pending').length} Pending
                </span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          {ONCHAIN_CLAIMS.some((c) => c.expiresAt && c.expiresAt <= '2026-04-30' && c.status === 'verified') && (
            <Alert className="mb-4 border-ascend-warning/30 bg-ascend-warning/10">
              <Icon name="streamline:warning-triangle" className="h-4 w-4 text-ascend-warning" />
              <AlertDescription className="text-ascend-warning">
                Some claims are expiring soon. Renew them to maintain uninterrupted access to credit facilities.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {ONCHAIN_CLAIMS.map((claim) => {
              const config = CLAIM_STATUS_CONFIG[claim.status];
              return (
                <div
                  key={claim.id}
                  className={`rounded-xl border p-4 transition-colors ${
                    claim.status === 'verified' ? 'border-green-200 bg-green-50/30' :
                    claim.status === 'pending' ? 'border-yellow-200 bg-yellow-50/30' :
                    claim.status === 'expired' ? 'border-red-200 bg-red-50/30' :
                    'border-border'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${
                        claim.status === 'verified' ? 'bg-green-100 text-green-700' :
                        claim.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {claim.icon}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm">{claim.label}</span>
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${config.className}`}>
                            {config.icon}
                            {config.label}
                          </span>
                        </div>
                        <p className="mt-0.5 text-xs text-muted-foreground">{claim.description}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span>Issuer: <span className="font-medium text-foreground">{claim.issuer}</span></span>
                    {claim.issuedAt && <span>Issued: {claim.issuedAt}</span>}
                    {claim.expiresAt && (
                      <span className={claim.expiresAt <= '2026-04-30' && claim.status === 'verified' ? 'text-ascend-warning font-medium' : ''}>
                        Expires: {claim.expiresAt}
                      </span>
                    )}
                    {claim.claimHash && (
                      <>
                        <button
                          onClick={() => setExpandedHash(expandedHash === claim.id ? null : claim.id)}
                          className="text-xs text-primary hover:underline"
                        >
                          {expandedHash === claim.id ? 'Hide hash' : 'View hash'}
                        </button>
                        {expandedHash === claim.id && (
                          <span className="font-mono text-xs">{claim.claimHash}</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Token Balances */}
      <Card id="balances" className="scroll-mt-28">
        <CardHeader className="p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Token Balances</CardTitle>
              <CardDescription>Current wallet holdings across all tokens.</CardDescription>
            </div>
            <button
              onClick={() => setShowAllTokens((v) => !v)}
              className="text-sm text-primary hover:underline"
            >
              {showAllTokens ? 'Hide zero balances' : 'Show all tokens'}
            </button>
          </div>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Filter by chain</label>
              <select
                value={tokenChainFilter}
                onChange={(e) => setTokenChainFilter(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm sm:w-44"
              >
                {tokenChains.map((chain) => (
                  <option key={chain} value={chain}>
                    {chain === 'all' ? 'All Chains' : chain}
                  </option>
                ))}
              </select>
            </div>
            <SearchBar
              value={tokenQuery}
              onChange={setTokenQuery}
              placeholder="Search token, symbol, chain..."
              className="w-full sm:w-72"
            />
          </div>

          {/* Desktop table */}
          <div className="hidden md:block">
            <DataTable<TokenBalance, TokenSortKey>
              columns={tokenColumns}
              rows={paginatedTokens}
              rowKey={(token) => token.symbol}
              sort={{
                key: tokenSortKey,
                direction: tokenSortDirection,
                onSort: onSortTokens,
              }}
              emptyMessage="No token balances match your filters."
            />
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {paginatedTokens.length === 0 ? (
              <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
                No token balances match your filters.
              </div>
            ) : (
              paginatedTokens.map((t) => (
                <div key={t.symbol} className="rounded-lg border border-border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                        {t.symbol.slice(0, 2)}
                      </div>
                      <div>
                        <span className="font-medium text-sm">{t.symbol}</span>
                        <p className="text-xs text-muted-foreground">{t.token}</p>
                      </div>
                    </div>
                    <span className={`text-xs font-medium ${t.change24h >= 0 ? 'text-ascend-success' : 'text-ascend-error'}`}>
                      {t.change24h >= 0 ? '+' : ''}
                      {t.change24h.toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono">
                      {t.balance} {t.symbol}
                    </span>
                    <span className="font-medium">{t.valueUsd}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          <PaginationControls
            page={tokenPage}
            setPage={setTokenPage}
            pageSize={tokenPageSize}
            setPageSize={setTokenPageSize}
            totalItems={filteredTokens.length}
            label="tokens"
            pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
          />

          <div className="mt-4 flex items-center justify-between rounded-lg bg-muted/50 p-3">
            <span className="text-sm font-medium">Total Portfolio Value</span>
            <span className="text-lg font-bold">{formatUsd(totalPortfolioValue)}</span>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}

// ─── Helper Components ──────────────────────────────────────────────────────────

function StatCard({
  title,
  value,
  icon,
  valueClassName,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
        {icon}
      </div>
      <div className={`mt-2 text-2xl sm:text-3xl font-bold ${valueClassName ?? ''}`}>{value}</div>
    </div>
  );
}

function InfoItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-lg bg-muted/50 p-3">
      <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function formatUsd(value: number): string {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
